import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { getBeginningOfNextDay, offDutyStatuses, type IState } from "../helpers";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

export type CycleRestConditions = {
    avail: number | undefined;
    isOffDuty: boolean;
    pauseCountingCycleOff: boolean;
    otherRulesetActive: boolean;
    isPreQualify: boolean;
    stateKind: "OnDuty" | "OffDutyPaused" | "OffDutyOtherRuleset" | "OffDutyPreQualify" | "OffDutySliding";
};

export class CycleRestCalculator extends AvailabilityCalculator {
    private conditions(state: IState): CycleRestConditions {
        const avail = discreteAvailabilityFunctions.CycleRest(state, false);
        const isOffDuty = offDutyStatuses.indexOf(state.current?.status) !== -1;
        const pauseCountingCycleOff = state.cycle.pauseCountingCycleOff ?? false;
        const otherRulesetActive = (state.cycleRestartDatesFromOtherRulesets?.length ?? 0) > 0
            && state.cycleRestartDatesFromOtherRulesets!.some(d => d.getTime() <= state.nextDate.getTime());
        const cycleRestMinimum = state.config.cycle?.cycleRestMinimum;
        const cycleConsecutiveOffCount = state.cycle.cycleConsecutiveOffCount ?? 0;
        const isPreQualify = cycleRestMinimum !== undefined && cycleConsecutiveOffCount < cycleRestMinimum;

        let stateKind: CycleRestConditions["stateKind"];
        if (!isOffDuty)            stateKind = "OnDuty";
        else if (pauseCountingCycleOff) stateKind = "OffDutyPaused";
        else if (otherRulesetActive)    stateKind = "OffDutyOtherRuleset";
        else if (isPreQualify)          stateKind = "OffDutyPreQualify";
        else                            stateKind = "OffDutySliding";

        return { avail, isOffDuty, pauseCountingCycleOff, otherRulesetActive, isPreQualify, stateKind };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(_state: IState): boolean {
        return true;
    }

    exhaustion(state: IState): Date | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        if (c.avail > 0) {
            return new Date(state.nextDate.getTime() + c.avail);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        const factors: Record<string, TAvailabilityFactorEntry> = {};

        switch (c.stateKind) {
            case "OnDuty":
            case "OffDutyPaused":
                return {};

            case "OffDutyOtherRuleset": {
                const completedRestPeriodDates = state.cycle.completedRestPeriodDates;
                const isSliding = completedRestPeriodDates.length > 0
                    && completedRestPeriodDates[completedRestPeriodDates.length - 1].getTime() === state.nextDate.getTime();
                if (isSliding) {
                    const nextDayBoundaryDate = getBeginningOfNextDay(state.currentDate, state.toLocalTime, state.toUtcTime);
                    factors.nextDayBoundary = { factor: () => nextDayBoundaryDate, label: "day boundary" };
                } else {
                    factors.nextDayBoundary = { factor: () => undefined, label: "day boundary" };
                }
                return factors;
            }

            case "OffDutyPreQualify": {
                // formula = ceiling(T_restart + 13d) − evalTime, rate −1, no midnight jumps.
                // Genuine inflections: restPeriodComplete (when count reaches cycleRestMinimum,
                // fillRestPeriodsEndDates fires and shifts the ceiling) and cycleRestart.
                // Both anchors are stable: formula = evalTime + (threshold − count), and as evalTime
                // advances by Δ, count increases by Δ (continuous off-duty), keeping the sum constant.
                const cycleRestMinimum = state.config.cycle!.cycleRestMinimum!;
                const cycleRestartMinimum = state.config.cycle?.cycleRestartMinimum;
                const cycleConsecutiveOffCount = state.cycle.cycleConsecutiveOffCount ?? 0;

                const restPeriodCompleteDate = new Date(
                    state.nextDate.getTime() + (cycleRestMinimum - cycleConsecutiveOffCount));
                factors.restPeriodComplete = { factor: () => restPeriodCompleteDate, label: "rest period complete" };

                let cycleRestartDate: Date | undefined = undefined;
                if (cycleRestartMinimum !== undefined && cycleConsecutiveOffCount < cycleRestartMinimum) {
                    cycleRestartDate = new Date(state.nextDate.getTime() + (cycleRestartMinimum - cycleConsecutiveOffCount));
                }
                factors.cycleRestart = { factor: () => cycleRestartDate, label: "cycle restart" };

                return factors;
            }

            case "OffDutySliding": {
                // CycleRest discrete = ceiling(completedRestPeriodDates[0] + cycleRestDays days) − state.nextDate.
                // In OffDutySliding the engine continuously updates completedRestPeriodDates[0] = state.nextDate
                // (hosCalculator.ts:1564 via fillRestPeriodsEndDates), so avail = ceiling(state.nextDate
                // + cycleRestDays days) − state.nextDate — a sawtooth that jumps +24h whenever the
                // ceiling rolls forward to the next local midnight. The cycle restart fires when
                // cycleConsecutiveOffCount reaches cycleRestartMinimum, but it does NOT produce a
                // CycleRest discrete inflection: the engine fires startNewCycle at state.nextDate and
                // re-anchors completedRestPeriodDates[0] = state.nextDate, leaving the avail formula
                // continuous across the restart. The only genuine inflection is the next ceiling
                // midnight, anchored to the last real log boundary (state.currentDate is stable
                // across evalTime advances within a log phase).
                const completedRestPeriodDates = state.cycle.completedRestPeriodDates;
                const isSliding = completedRestPeriodDates.length > 0
                    && completedRestPeriodDates[completedRestPeriodDates.length - 1].getTime() === state.nextDate.getTime();

                const nextDayBoundaryDate: Date | undefined = isSliding
                    ? getBeginningOfNextDay(state.currentDate, state.toLocalTime, state.toUtcTime)
                    : undefined;
                factors.nextDayBoundary = { factor: () => nextDayBoundaryDate, label: "day boundary" };

                return factors;
            }
        }
    }
}

export const extractCycleRestConditions = (state: IState): CycleRestConditions =>
    new CycleRestCalculator().extractConditions(state) as CycleRestConditions;

export const computeCycleRestResult = (state: IState): TComputedAvailabilityResult | undefined =>
    new CycleRestCalculator().compute(state);
